import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';
import { TOKEN_2022_PROGRAM_ID, TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { collection, doc, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { DONATION_TOKENS } from '../../utils/donationTokens';
import { setWalletUsername } from '../../utils/walletUsername';
import { isVerifiedPayment, formatUnits } from '../../utils/activityEvents';
import CreatorCollectionManager from '../CreatorCollectionManager';
import WalletTokenTransfer from '../WalletTokenTransfer';
import NotificationInbox from '../NotificationInbox';
import '../MuultiStyle.css';
import './UserWalletDetailPage.css';

const CRUMB_NFT_EXPLORER_URL = 'https://explorer.thesimulacrum.xyz/';
const COMPONENT_ROUTE_SEGMENTS = {
    text: 'c',
    video: 'v',
    link: 'l',
    image: 'i',
    mint: 'm',
    mintnft: 'm',
    donation: 'd',
};

const shortAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
};

const formatSol = (value) => {
    if (value == null) return 'Not loaded';
    return new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 9,
    }).format(value);
};

const formatTokenAmount = (amount, decimals = 9) => {
    if (typeof amount !== 'number' || !Number.isFinite(amount)) return 'Not loaded';
    const maximumFractionDigits = Math.max(0, Math.min(12, Number(decimals ?? 9)));
    return new Intl.NumberFormat(undefined, {
        maximumFractionDigits,
    }).format(amount);
};

const formatAmount = (amount, currency = 'SOL') => {
    if (typeof amount !== 'number' || !Number.isFinite(amount)) return currency || '';
    return `${new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 9,
    }).format(amount)} ${currency || ''}`.trim();
};

const timestampMs = (value) => {
    if (!value) return 0;
    if (typeof value?.toMillis === 'function') return value.toMillis();
    if (typeof value?.seconds === 'number') return value.seconds * 1000;
    const parsed = Date.parse(String(value));
    return Number.isFinite(parsed) ? parsed : 0;
};

const formatWhen = (value) => {
    const ms = timestampMs(value);
    return ms ? new Date(ms).toLocaleString() : 'Pending timestamp';
};

const transactionUrl = (signature, cluster = '') => {
    if (!signature) return '';
    const suffix = cluster ? `?cluster=${encodeURIComponent(cluster)}` : '';
    return `https://explorer.solana.com/tx/${signature}${suffix}`;
};

const chunkArray = (items, size) => {
    const chunks = [];
    for (let i = 0; i < items.length; i += size) {
        chunks.push(items.slice(i, i + size));
    }
    return chunks;
};

const componentRouteSegment = (componentType) => {
    const normalized = String(componentType || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
    return COMPONENT_ROUTE_SEGMENTS[normalized] || '';
};

const historyPageHref = (row, ownerSpace) => {
    const muultiName = String(ownerSpace?.muultiName || row.pageMuultiName || '').trim();
    if (!muultiName) return '';

    const baseHref = `/${encodeURIComponent(muultiName)}`;
    const componentId = String(row.componentId || '').trim();
    const segment = componentRouteSegment(row.componentType);
    if (!componentId || !segment) return baseHref;

    return `${baseHref}/${segment}/${encodeURIComponent(componentId)}`;
};

const loadOwnerSpaces = async (rows) => {
    const ownerPublicKeys = Array.from(new Set(
        rows
            .map((row) => String(row.pageOwnerPublicKey || '').trim())
            .filter(Boolean)
    ));
    if (!ownerPublicKeys.length) return new Map();

    const ownerSpaces = new Map();
    const chunks = chunkArray(ownerPublicKeys, 10);
    for (const chunk of chunks) {
        const snap = await getDocs(query(
            collection(db, 'users'),
            where('publicKey', 'in', chunk)
        ));
        snap.forEach((docSnap) => {
            const data = docSnap.data() || {};
            const publicKey = String(data.publicKey || '').trim();
            const muultiName = String(data.muultiName || '').trim();
            if (!publicKey || !muultiName) return;
            ownerSpaces.set(publicKey, {
                muultiName,
                pageTitle: data.pageTitle || muultiName,
            });
        });
    }
    return ownerSpaces;
};

const loadSplTokenBalances = async (connection, publicKey) => {
    const configuredMints = new Set(
        (DONATION_TOKENS || [])
            .filter((token) => !token.isNative && token.mint)
            .map((token) => String(token.mint))
    );
    if (!configuredMints.size) return new Map();

    const programIds = [TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID];
    const settled = await Promise.allSettled(
        programIds.map((programId) =>
            connection.getParsedTokenAccountsByOwner(
                publicKey,
                { programId },
                'confirmed'
            )
        )
    );
    if (!settled.some((result) => result.status === 'fulfilled')) {
        const reason = settled.find((result) => result.status === 'rejected')?.reason;
        throw new Error(reason?.message || 'Unable to load token balances.');
    }

    const balances = new Map();
    settled.forEach((result) => {
        if (result.status !== 'fulfilled') return;
        (result.value?.value || []).forEach((account) => {
            const info = account?.account?.data?.parsed?.info || {};
            const mint = String(info.mint || '');
            if (!configuredMints.has(mint)) return;

            const tokenAmount = info.tokenAmount || {};
            const amount = Number(tokenAmount.uiAmountString ?? tokenAmount.uiAmount ?? 0);
            const existing = balances.get(mint) || {
                amount: 0,
                decimals: tokenAmount.decimals,
                accountCount: 0,
            };
            balances.set(mint, {
                amount: existing.amount + (Number.isFinite(amount) ? amount : 0),
                decimals: existing.decimals ?? tokenAmount.decimals,
                accountCount: existing.accountCount + 1,
            });
        });
    });

    return balances;
};

function WalletAddressCopyButton({ address }) {
    const [copyState, setCopyState] = useState('');
    useEffect(() => {
        if (copyState !== 'copied') return undefined;
        const timeout = setTimeout(() => setCopyState(''), 2000);
        return () => clearTimeout(timeout);
    }, [copyState]);
    const copyAddress = async () => {
        if (copyState === 'copying') return;
        setCopyState('copying');
        try {
            await navigator.clipboard.writeText(address);
            setCopyState('copied');
        } catch {
            setCopyState('failed');
        }
    };
    return <>
        <button type="button" className="muulti-button wallet-detail-copy"
            aria-label="Copy wallet address" title={copyState === 'copied' ? 'Copied!' : 'Copy full wallet address'}
            disabled={copyState === 'copying'} onClick={copyAddress}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                {copyState === 'copied' ? <path d="M5 12l4 4L19 6" />
                    : <><rect x="9" y="9" width="11" height="11" rx="1" /><path d="M15 9V4H4v11h5" /></>}
            </svg>
        </button>
        {copyState === 'copied' && <span className="wallet-detail-copy-status" role="status">Wallet address copied.</span>}
        {copyState === 'failed' && <span className="wallet-detail-copy-error" role="alert">
            Could not copy automatically. Select the address and copy it manually.
        </span>}
    </>;
}

export default function UserWalletDetailPage() {
    const wallet = useWallet();
    const { connected, publicKey } = wallet;
    const walletRef = useRef(wallet);
    walletRef.current = wallet;
    const { connection } = useConnection();
    const connectionRef = useRef(connection);
    connectionRef.current = connection;
    const transferBusyRef = useRef(false);
    const [transferBusy, setTransferBusy] = useState(false);
    const walletIsCurrent = useCallback((sender, rpcConnection) => (
        walletRef.current.connected && walletRef.current.publicKey?.toBase58?.() === sender &&
        connectionRef.current === rpcConnection
    ), []);
    const runTransferAction = useCallback(async (action) => {
        if (transferBusyRef.current) throw new Error('Another transfer is in progress. Please wait.');
        transferBusyRef.current = true;
        setTransferBusy(true);
        try {
            return await action();
        } finally {
            transferBusyRef.current = false;
            setTransferBusy(false);
        }
    }, []);
    const [solBalance, setSolBalance] = useState(null);
    const [tokenBalances, setTokenBalances] = useState(new Map());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [lastUpdated, setLastUpdated] = useState(null);
    const [historyRows, setHistoryRows] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState('');
    const historyRequestRef = useRef(0);

    const walletAddress = publicKey?.toBase58?.() || publicKey?.toString?.() || '';
    const [currentUsername, setCurrentUsername] = useState('');
    const [newUsername, setNewUsername] = useState('');
    const [usernameLoading, setUsernameLoading] = useState(true);
    const [usernameSaving, setUsernameSaving] = useState(false);
    const [usernameError, setUsernameError] = useState('');
    const [usernameMessage, setUsernameMessage] = useState('');
    const usernameRequestRef = useRef(null);

    useEffect(() => {
        const scope = { active: true };
        usernameRequestRef.current = scope;
        setCurrentUsername('');
        setNewUsername('');
        setUsernameError('');
        setUsernameMessage('');
        setUsernameSaving(false);
        setUsernameLoading(!!walletAddress);
        if (!walletAddress) return () => { scope.active = false; };
        const unsubscribe = onSnapshot(doc(db, 'users', walletAddress), (snap) => {
            if (!scope.active) return;
            setCurrentUsername(snap.data()?.muultiName || '');
            setUsernameLoading(false);
        }, () => {
            if (!scope.active) return;
            setUsernameError('Unable to load your username. Reload this page to try again.');
            setUsernameLoading(false);
        });
        return () => {
            scope.active = false;
            unsubscribe();
        };
    }, [walletAddress]);

    const handleUsernameSubmit = async (event) => {
        event.preventDefault();
        if (usernameSaving || usernameLoading) return;
        const scope = usernameRequestRef.current;
        const isCurrent = () => scope?.active &&
            walletRef.current?.publicKey?.toBase58?.() === walletAddress;
        setUsernameSaving(true);
        setUsernameError('');
        setUsernameMessage('');
        try {
            const result = await setWalletUsername(wallet, newUsername, { isCurrent });
            if (!isCurrent()) return;
            setCurrentUsername(result.muultiName);
            setNewUsername('');
            setUsernameMessage(`Username saved. ${result.changesRemaining} of 2 changes remaining in the current 7-day period.`);
        } catch (err) {
            if (!isCurrent()) return;
            const retryAt = err?.details?.nextChangeAt;
            setUsernameError((err?.message || 'Unable to update your username.') +
                (retryAt ? ` Try again after ${new Date(retryAt).toLocaleString()}.` : ''));
        } finally {
            if (isCurrent()) setUsernameSaving(false);
        }
    };

    const loadBalances = useCallback(async () => {
        if (!connected || !publicKey || !connection) {
            setSolBalance(null);
            setTokenBalances(new Map());
            setError('');
            setLastUpdated(null);
            return;
        }

        setLoading(true);
        setError('');
        try {
            const [lamports, splBalances] = await Promise.all([
                connection.getBalance(publicKey, 'confirmed'),
                loadSplTokenBalances(connection, publicKey),
            ]);
            if (!walletIsCurrent(publicKey.toBase58(), connection)) return;
            setSolBalance(lamports / LAMPORTS_PER_SOL);
            setTokenBalances(splBalances);
            setLastUpdated(new Date());
        } catch (err) {
            if (!walletIsCurrent(publicKey.toBase58(), connection)) return;
            console.error('Wallet balance fetch error:', err);
            setError(err?.message || 'Unable to load wallet balance.');
        } finally {
            if (walletIsCurrent(publicKey.toBase58(), connection)) setLoading(false);
        }
    }, [connected, publicKey, connection, walletIsCurrent]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!connected || !publicKey || !connection) {
                setSolBalance(null);
                setTokenBalances(new Map());
                setError('');
                setLastUpdated(null);
                return;
            }

            setLoading(true);
            setError('');
            try {
                const [lamports, splBalances] = await Promise.all([
                    connection.getBalance(publicKey, 'confirmed'),
                    loadSplTokenBalances(connection, publicKey),
                ]);
                if (cancelled) return;
                setSolBalance(lamports / LAMPORTS_PER_SOL);
                setTokenBalances(splBalances);
                setLastUpdated(new Date());
            } catch (err) {
                if (cancelled) return;
                console.error('Wallet balance fetch error:', err);
                setError(err?.message || 'Unable to load wallet balance.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [connected, publicKey, connection]);

    const loadTransactionHistory = useCallback(async () => {
        const requestId = ++historyRequestRef.current;
        const isCurrent = () => requestId === historyRequestRef.current && walletRef.current.publicKey?.toBase58?.() === walletAddress;
        if (!walletAddress) {
            setHistoryRows([]);
            setHistoryError('');
            return;
        }

        setHistoryLoading(true);
        setHistoryError('');

        const collectionLoaders = [
            {
                key: 'donations',
                label: 'donation records',
                run: async () => {
                    const snap = await getDocs(query(
                        collection(db, 'donations'),
                        where('sender', '==', walletAddress)
                    ));
                    const rows = [];
                    snap.forEach((docSnap) => {
                        const data = docSnap.data() || {};
                        const receiver = String(data.receiver || '');
                        if (receiver && receiver === walletAddress) return;
                        const compId = String(data.compId || data.componentId || '');
                        rows.push({
                            id: `donation:${docSnap.id}`,
                            type: 'Donation',
                            title: data.campaignName || 'Donation component',
                            amountLabel: formatAmount(data.amount, data.currency || 'SOL'),
                            status: data.status === 'failed' ? 'failed' : 'unverified',
                            sourceKey: `donation:${docSnap.id}`,
                            when: data.createdAt,
                            txSig: data.txSig || '',
                            counterparty: receiver,
                            detail: compId ? `Component ${compId}` : '',
                            pageOwnerPublicKey: String(data.pageOwnerPublicKey || data.ownerPublicKey || data.pageOwnerId || receiver || ''),
                            pageMuultiName: data.pageMuultiName || data.muultiName || '',
                            componentId: compId,
                            componentType: 'donation',
                        });
                    });
                    return rows;
                },
            },
            {
                key: 'directPayments',
                label: 'direct payment records',
                run: async () => {
                    const snap = await getDocs(query(
                        collection(db, 'directPayments'),
                        where('payer', '==', walletAddress)
                    ));
                    const rows = [];
                    snap.forEach((docSnap) => {
                        const data = docSnap.data() || {};
                        const payTo = String(data.payTo || '');
                        if (payTo && payTo === walletAddress) return;
                        const amount = typeof data.amount === 'number' ? data.amount : data.amountSol;
                        const componentId = String(data.componentId || data.targetId || '');
                        const componentType = String(data.componentType || '');
                        rows.push({
                            id: `directPayments:${docSnap.id}`,
                            type: 'Direct Payment',
                            title: componentType ? `${componentType} gate unlock` : 'Gate unlock',
                            amountLabel: formatAmount(amount, data.currency || 'SOL'),
                            status: data.status === 'failed' ? 'failed' : 'unverified',
                            sourceKey: `directPayment:${docSnap.id}`,
                            when: data.createdAt,
                            txSig: data.txSig || '',
                            counterparty: payTo,
                            detail: componentId ? `Component ${componentId}` : '',
                            pageOwnerPublicKey: String(data.pageOwnerPublicKey || data.ownerPublicKey || payTo || ''),
                            pageMuultiName: data.pageMuultiName || data.muultiName || '',
                            componentId,
                            componentType,
                        });
                    });
                    return rows;
                },
            },
            {
                key: 'paymentReceipts',
                label: 'verified payment receipts',
                run: async () => {
                    const snap = await getDocs(query(collection(db, 'paymentReceipts'), where('payer', '==', walletAddress)));
                    const rows = [];
                    snap.forEach(docSnap => {
                        const data = docSnap.data() || {};
                        if (!isVerifiedPayment(data)) return;
                        const donation = data.kind === 'donation';
                        rows.push({
                            id: 'payment:' + docSnap.id, sourceKey: (donation ? 'donation:' : 'directPayment:') + docSnap.id,
                            verified: true, type: donation ? 'Donation' : 'Direct Payment',
                            title: donation ? 'Donation component' : 'Gate unlock',
                            amountLabel: formatUnits(data.amountRaw, data.decimals) + ' ' + data.currency,
                            status: 'confirmed', when: data.occurredAt || data.createdAt, txSig: data.txSig,
                            cluster: data.cluster, counterparty: data.receiver || data.payTo || '',
                            componentId: data.componentId || data.compId || '', componentType: donation ? 'donation' : data.componentType,
                            pageOwnerPublicKey: data.spaceOwnerUid || '', pageMuultiName: data.pageMuultiName || '',
                        });
                    });
                    return rows;
                },
            },
            {
                key: 'mints',
                label: 'confirmed NFT mint records',
                run: async () => {
                    const snap = await getDocs(query(collection(db, 'mints'), where('ownerUid', '==', walletAddress)));
                    const rows = [];
                    snap.forEach(docSnap => {
                        const data = docSnap.data() || {};
                        rows.push({
                            id: 'mint:' + docSnap.id, verified: true, type: 'NFT Mint', title: 'NFT component mint',
                            amountLabel: data.priceAtomic == null ? '' : formatUnits(data.priceAtomic, data.paymentDecimals ?? 9) + ' ' + (data.paymentToken || 'SOL'),
                            status: 'confirmed', when: data.createdAt, txSig: data.signature || '',
                            cluster: data.cluster || '', counterparty: data.creatorUserId === walletAddress ? '' : data.creatorUserId || '',
                            detail: data.assetAddress ? 'Asset ' + shortAddress(data.assetAddress) : '',
                            componentId: data.componentId || '', componentType: 'mint', pageOwnerPublicKey: data.creatorUserId || '',
                        });
                    });
                    return rows;
                },
            },
        ];

        try {
            const settled = await Promise.allSettled(collectionLoaders.map((loader) => loader.run()));
            const rows = [];
            const failed = [];
            settled.forEach((result, index) => {
                if (result.status === 'fulfilled') {
                    rows.push(...result.value);
                } else {
                    if (!collectionLoaders[index].optional) {
                        failed.push(collectionLoaders[index].label);
                    }
                    console.warn(`Wallet history ${collectionLoaders[index].key} load failed:`, result.reason);
                }
            });

            try {
                const ownerSpaces = await loadOwnerSpaces(rows);
                rows.forEach((row) => {
                    const ownerSpace = ownerSpaces.get(String(row.pageOwnerPublicKey || '').trim());
                    row.pageHref = historyPageHref(row, ownerSpace);
                    row.pageLabel = row.pageMuultiName || ownerSpace?.pageTitle || ownerSpace?.muultiName || '';
                });
            } catch (err) {
                console.warn('Wallet history page link resolution failed:', err);
            }

            if (!isCurrent()) return;
            const deduplicated = new Map();
            rows.forEach(row => {
                const key = row.sourceKey || row.id;
                if (!deduplicated.has(key) || row.verified) deduplicated.set(key, row);
            });
            setHistoryRows(Array.from(deduplicated.values()).sort((a, b) => timestampMs(b.when) - timestampMs(a.when)));
            setHistoryError(failed.length ? `Could not load ${failed.join(', ')}.` : '');
        } catch (err) {
            console.error('Wallet history fetch error:', err);
            if (!isCurrent()) return;
            setHistoryRows([]);
            setHistoryError(err?.message || 'Unable to load wallet history.');
        } finally {
            if (isCurrent()) setHistoryLoading(false);
        }
    }, [walletAddress]);

    useEffect(() => {
        loadTransactionHistory();
    }, [loadTransactionHistory]);

    const balanceRows = useMemo(() => {
        return (DONATION_TOKENS || []).map((token) => {
            const isNativeSol = token.isNative || token.symbol === 'SOL';
            const solBalanceLabel = solBalance == null ? 'Not loaded' : `${formatSol(solBalance)} SOL`;
            const tokenBalance = token.mint ? tokenBalances.get(String(token.mint)) : null;
            const tokenBalanceLabel = tokenBalance
                ? `${formatTokenAmount(tokenBalance.amount, token.decimals ?? tokenBalance.decimals)} ${token.symbol}`
                : `0 ${token.symbol}`;
            const solStatusLabel = error
                ? 'Balance unavailable'
                : loading
                    ? 'Refreshing'
                    : solBalance == null
                        ? 'Waiting for RPC'
                        : 'Live SOL balance';
            const tokenStatusLabel = error
                ? 'Balance unavailable'
                : loading
                    ? 'Refreshing'
                    : tokenBalance?.accountCount
                        ? `${tokenBalance.accountCount} token account${tokenBalance.accountCount === 1 ? '' : 's'}`
                        : 'No token account found';
            return {
                ...token,
                balanceLabel: isNativeSol ? solBalanceLabel : tokenBalanceLabel,
                statusLabel: isNativeSol ? solStatusLabel : tokenStatusLabel,
            };
        });
    }, [solBalance, tokenBalances, loading, error]);

    return (
        <div className="muulti-container">
            <div className="wallet-detail-page">
                <div className="detail-page-return-link-container">
                    <Link className="muulti-button detail-page-return-link" to="/">
                        Back to editor
                    </Link>
                </div>

                <section className="wallet-detail-card wallet-detail-header">
                    <div className="wallet-detail-identity">
                        <h1>User Wallet</h1>
                        <div className="wallet-detail-connected-wallet">
                            <p className="wallet-detail-muted">
                                {walletAddress
                                    ? `Connected wallet ${walletAddress}`
                                    : 'Connect a wallet to view balances.'}
                            </p>
                            {walletAddress && <WalletAddressCopyButton key={walletAddress} address={walletAddress} />}
                        </div>
                    </div>

                    <div className="wallet-detail-actions">
                        <WalletMultiButton />
                        {walletAddress ? (
                            <Link
                                className="muulti-button wallet-detail-external-link"
                                to="/wallet-detail/followers"
                            >
                                View Followers
                            </Link>
                        ) : null}
                        <a
                            className="muulti-button wallet-detail-external-link"
                            href={CRUMB_NFT_EXPLORER_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Crumb NFT Explorer
                        </a>
                    </div>
                </section>

                {walletAddress && (
                    <>
                        <section className="wallet-detail-card" aria-labelledby="wallet-username-heading">
                            <div className="wallet-detail-row">
                                <h2 id="wallet-username-heading">Wallet Username</h2>
                            </div>
                            <p className="wallet-detail-muted">
                                {usernameLoading ? 'Loading username...' : currentUsername
                                    ? `Current username: ${currentUsername}` : 'Choose a unique wallet username.'}
                            </p>
                            <details className="wallet-username-details" key={walletAddress}>
                                <summary className="wallet-detail-summary">Change username</summary>
                                <form className="wallet-username-form" onSubmit={handleUsernameSubmit}>
                                    <label htmlFor="wallet-username">
                                        New username
                                        <input
                                            id="wallet-username"
                                            className="muulti-input"
                                            value={newUsername}
                                            onChange={(e) => setNewUsername(e.target.value)}
                                            required
                                            minLength={3}
                                            maxLength={27}
                                            pattern="[A-Za-z0-9_-]+"
                                            title="3-27 characters: letters, numbers, underscores and dashes."
                                            autoComplete="off"
                                            disabled={usernameLoading || usernameSaving}
                                            aria-describedby="wallet-username-help"
                                        />
                                    </label>
                                    <p id="wallet-username-help" className="wallet-detail-muted">
                                        Use 3-27 letters, numbers, underscores or dashes. You can change your username
                                        twice in any 7-day period. This changes your public page URL; previous links
                                        will stop working. Edit your site title in Header Settings.
                                    </p>
                                    <button
                                        type="submit"
                                        className="muulti-button"
                                        disabled={usernameLoading || usernameSaving || !newUsername.trim() || newUsername.trim() === currentUsername}
                                    >
                                        {usernameSaving ? 'Saving...' : 'Save Username'}
                                    </button>
                                </form>
                                {usernameError && <p className="wallet-detail-error" role="alert">{usernameError}</p>}
                                {usernameMessage && <p className="wallet-detail-muted" role="status">{usernameMessage}</p>}
                            </details>
                        </section>

                        <section className="wallet-detail-card">
                            <NotificationInbox wallet={wallet} />
                        </section>

                        <section className="wallet-detail-card" aria-labelledby="wallet-collections-heading">
                            <div className="wallet-detail-row">
                                <div>
                                    <h2 id="wallet-collections-heading">Collections</h2>
                                    <p className="wallet-detail-muted">
                                        Create collections, review their on-chain addresses, and choose the default
                                        collection for new Digital Asset Offers.
                                    </p>
                                </div>
                            </div>
                            <CreatorCollectionManager spaceId={walletAddress} />
                            <p className="wallet-detail-muted">
                                This list contains collections created with this wallet through the collection tool. Collectibles held
                                by the wallet do not grant permission to administer their collections.
                            </p>
                        </section>

                        <section className="wallet-detail-card">
                            <div className="wallet-detail-row wallet-detail-row--header">
                                <div>
                                    <h2>Balances</h2>
                                    <p className="wallet-detail-address">{walletAddress}</p>
                                </div>
                                <button
                                    type="button"
                                    className="muulti-button wallet-detail-refresh"
                                    onClick={loadBalances}
                                    disabled={loading || transferBusy}
                                >
                                    {loading ? 'Refreshing...' : 'Refresh'}
                                </button>
                            </div>

                            {error && <p className="wallet-detail-error">{error}</p>}
                            {lastUpdated && (
                                <p className="wallet-detail-muted">
                                    Updated {lastUpdated.toLocaleString()}
                                </p>
                            )}

                            <div className="wallet-balance-list">
                                {balanceRows.map((token) => (
                                    <div className="wallet-balance-row" key={token.symbol}>
                                        <div className="wallet-token-meta">
                                            <span className="wallet-token-symbol">{token.symbol}</span>
                                            <span className="wallet-token-name">{token.name}</span>
                                        </div>
                                        <div className="wallet-balance-value">{token.balanceLabel}</div>
                                        <div className="wallet-balance-note">{token.statusLabel}</div>
                                        <WalletTokenTransfer
                                            key={`${walletAddress}:${token.symbol}:${connection?.rpcEndpoint || ''}`}
                                            token={token}
                                            wallet={wallet}
                                            connection={connection}
                                            busy={transferBusy}
                                            runTransferAction={runTransferAction}
                                            walletIsCurrent={walletIsCurrent}
                                            onConfirmed={loadBalances}
                                        />
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section className="wallet-detail-card">
                            <div className="wallet-detail-row wallet-detail-row--header">
                                <div>
                                    <h2>MuultiSpace Transaction History</h2>
                                    <p className="wallet-detail-muted">
                                        Donations, NFT mints, and direct payment unlocks saved for this wallet.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="muulti-button wallet-detail-refresh"
                                    onClick={loadTransactionHistory}
                                    disabled={historyLoading}
                                >
                                    {historyLoading ? 'Loading...' : 'Refresh History'}
                                </button>
                            </div>

                            {historyError && <p className="wallet-detail-error">{historyError}</p>}

                            {historyLoading && !historyRows.length ? (
                                <p className="wallet-detail-muted">Loading transaction history...</p>
                            ) : historyRows.length ? (
                                <div className="wallet-history-list">
                                    {historyRows.map((row) => (
                                        <div className="wallet-history-row" key={row.id}>
                                            <div className="wallet-history-main">
                                                <span className="wallet-history-type">{row.type}</span>
                                                <strong>{row.title}</strong>
                                                <span className="wallet-detail-muted">{formatWhen(row.when)}</span>
                                                {row.detail && (
                                                    <span className="wallet-detail-muted">{row.detail}</span>
                                                )}
                                            </div>
                                            <div className="wallet-history-meta">
                                                <span className={`wallet-history-status status-${String(row.status).toLowerCase()}`}>
                                                    {row.status}
                                                </span>
                                                <span>{row.amountLabel}</span>
                                                {row.counterparty && (
                                                    <span className="wallet-detail-muted">
                                                        To {shortAddress(row.counterparty)}
                                                    </span>
                                                )}
                                                {row.pageHref ? (
                                                    <Link to={row.pageHref}>
                                                        View Page
                                                    </Link>
                                                ) : null}
                                                {row.txSig ? (
                                                    <a
                                                        href={transactionUrl(row.txSig, row.cluster)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        View Tx
                                                    </a>
                                                ) : (
                                                    <span className="wallet-detail-muted">No tx link</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="wallet-detail-muted">
                                    No saved MuultiSpace transactions found for this wallet yet.
                                </p>
                            )}
                        </section>
                    </>
                )}
            </div>
        </div>
    );
}
